import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info } from "lucide-react";

const ORG_NUMBER_REGEX = /^\d{2}[2-9]\d{3}-\d{4}$/;

const COMMON_EMAIL_PROVIDERS = new Set([
  "gmail.com", "googlemail.com", "outlook.com", "hotmail.com",
  "yahoo.com", "yahoo.se", "live.com", "icloud.com", "me.com",
  "protonmail.com", "proton.me", "msn.com", "aol.com",
  "telia.com", "bredband.net", "comhem.se", "spray.se",
]);

/** Domain only (no scheme) — https:// is added when saving. */
function suggestWebsiteFromEmail(email: string): string | null {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain || COMMON_EMAIL_PROVIDERS.has(domain)) return null;
  return domain;
}

/** Turn user input into a full URL for Firestore/API (zod .url()). */
function normalizeWebsiteUrlForSave(raw: string): string | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function isValidSwedishOrgNumber(value: string): boolean {
  const digits = value.replace(/-/g, "");
  if (!/^\d{10}$/.test(digits)) return false;
  if (parseInt(digits[2]!) < 2) return false;
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    let digit = parseInt(digits[i]!);
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

function formatOrgNumber(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length > 6) {
    return `${digits.slice(0, 6)}-${digits.slice(6, 10)}`;
  }
  return digits;
}

interface CreateBusinessStepProps {
  onSubmit: (data: {
    company: string;
    companyNumber: string;
    website: string;
  }) => void;
  errorMsg: string | null;
  loading: boolean;
  userEmail: string;
}

export default function CreateBusinessStep({
  onSubmit,
  errorMsg,
  loading,
  userEmail,
}: CreateBusinessStepProps) {
  const { t } = useTranslation("onboarding");
  const [formData, setFormData] = useState({
    company: "",
    companyNumber: "",
    website: "",
  });
  const [orgNumberError, setOrgNumberError] = useState<string | null>(null);

  useEffect(() => {
    if (!userEmail) return;
    const domain = suggestWebsiteFromEmail(userEmail);
    if (!domain) return;
    setFormData((prev) => {
      if (prev.website) return prev;
      return { ...prev, website: domain };
    });
  }, [userEmail]);

  const emailDerivedDomain = userEmail
    ? suggestWebsiteFromEmail(userEmail)
    : null;
  const showWebsiteSuggestionHint =
    emailDerivedDomain != null &&
    formData.website.trim().toLowerCase() === emailDerivedDomain;

  const handleInputChange = (field: string, value: string) => {
    if (field === "companyNumber") {
      const formatted = formatOrgNumber(value);
      setFormData((prev) => ({ ...prev, [field]: formatted }));
      if (orgNumberError) setOrgNumberError(null);
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.companyNumber || formData.companyNumber.trim() === "") {
      setOrgNumberError(t("createBusiness.orgNumberRequired"));
      return;
    }

    if (!ORG_NUMBER_REGEX.test(formData.companyNumber)) {
      setOrgNumberError(t("createBusiness.orgNumberInvalid"));
      return;
    }

    if (!isValidSwedishOrgNumber(formData.companyNumber)) {
      setOrgNumberError(t("createBusiness.orgNumberIncorrect"));
      return;
    }

    setOrgNumberError(null);
    onSubmit({
      ...formData,
      website: normalizeWebsiteUrlForSave(formData.website) ?? "",
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-[15px] leading-relaxed text-muted-foreground">
        {t("createBusiness.description")}
      </p>

      {errorMsg && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm font-semibold text-destructive"
        >
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="company">{t("createBusiness.companyLabel")}</Label>
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => handleInputChange("company", e.target.value)}
            placeholder={t("createBusiness.companyPlaceholder")}
            autoComplete="organization"
            className="h-11"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyNumber">{t("createBusiness.orgNumberLabel")}</Label>
          <Input
            id="companyNumber"
            value={formData.companyNumber}
            onChange={(e) =>
              handleInputChange("companyNumber", e.target.value)
            }
            placeholder={t("createBusiness.orgNumberPlaceholder")}
            autoComplete="off"
            maxLength={11}
            className={`h-11 ${orgNumberError ? "border-destructive focus-visible:ring-destructive" : ""}`}
          />
          {orgNumberError && (
            <p className="text-sm text-destructive">{orgNumberError}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">{t("createBusiness.websiteLabel")}</Label>
          <Input
            id="website"
            type="text"
            inputMode="url"
            value={formData.website}
            onChange={(e) => handleInputChange("website", e.target.value)}
            placeholder={t("createBusiness.websitePlaceholder")}
            autoComplete="url"
            className="h-11"
          />
          {showWebsiteSuggestionHint && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="h-3 w-3" />
              {t("createBusiness.websiteSuggestion")}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="h-11 w-full bg-foreground text-background hover:bg-foreground/90"
        >
          {loading ? t("createBusiness.creating") : t("createBusiness.submit")}
        </Button>
      </form>
    </div>
  );
}
